import { z } from 'zod';
/**
 * Request/response validation shared by the API handlers and the web client.
 * The handlers parse every request body through these, so a malformed payload
 * fails at the edge with a 400 rather than halfway through a DynamoDB write.
 */
export const seniorityEnum = z.enum(['intern', 'junior', 'mid', 'senior', 'staff', 'principal']);
export const jobStatusEnum = z.enum(['draft', 'open', 'closed']);
export const interviewStageEnum = z.enum(['soft_skill', 'situational', 'technical']);
/** The order sections always run in. */
export const STAGE_ORDER = ['soft_skill', 'situational', 'technical'];
export const STAGE_LABELS = {
    soft_skill: 'Soft skills',
    situational: 'Situational',
    technical: 'Technical',
};
/**
 * Suggested size of a role's question set. Guidance for the role form, not a
 * rule: a first-round screen that runs past 45 minutes loses candidates, so
 * junior and mid roles get a shorter technical section than senior ones.
 */
export function recommendedStructure(seniority) {
    const senior = seniority === 'senior' || seniority === 'staff' || seniority === 'principal';
    return {
        durationMinutes: senior ? 45 : 30,
        counts: {
            soft_skill: [2, 3],
            situational: [2, 3],
            technical: senior ? [5, 6] : [4, 4],
        },
    };
}
/**
 * Size limits on a role and an invite. Named here so the web forms enforce the
 * same numbers the API validates against, instead of finding out from a 400.
 */
export const JOB_LIMITS = {
    titleLength: [2, 150],
    departmentLength: 100,
    locationLength: 120,
    descriptionLength: [20, 20_000],
    competencies: 12,
    competencyLength: 80,
    questions: 30,
    questionLength: [5, 1000],
    followUpHintLength: 500,
};
/**
 * Languages the role form offers. The interview agent speaks the one chosen,
 * but only if the voice and speech model on the published ElevenLabs agent
 * support it - which is why the list is short and the form says to test first.
 */
export const INTERVIEW_LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'Hindi' },
    { code: 'es', label: 'Spanish' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'ar', label: 'Arabic' },
];
export function languageLabel(code) {
    return INTERVIEW_LANGUAGES.find((language) => language.code === code)?.label ?? code;
}
export const CANDIDATE_LIMITS = {
    nameLength: 150,
    emailLength: 254,
    backgroundLength: 10_000,
    resumeTextLength: [50, 60_000],
};
export const interviewQuestionInputSchema = z.object({
    text: z.string().min(JOB_LIMITS.questionLength[0]).max(JOB_LIMITS.questionLength[1]),
    competency: z.string().min(1).max(JOB_LIMITS.competencyLength),
    followUpHint: z.string().max(JOB_LIMITS.followUpHintLength).optional(),
    mustAsk: z.boolean().default(false),
    stage: interviewStageEnum.default('technical'),
});
/**
 * What a job is made of, with no defaults.
 *
 * Defaults belong to creation only. The update schema used to be
 * `createJobSchema.partial()`, which keeps them - so a PATCH that did not
 * mention seniority, duration, language or status came back from parsing with
 * `mid`, 20 minutes, `en` and `open` filled in, and the route spread those over
 * the stored job. Closing a role silently reset its seniority and length; tagging
 * a senior role's questions made it a mid role.
 */
const jobFields = {
    title: z.string().min(JOB_LIMITS.titleLength[0]).max(JOB_LIMITS.titleLength[1]),
    department: z.string().max(JOB_LIMITS.departmentLength).optional(),
    location: z.string().max(JOB_LIMITS.locationLength).optional(),
    seniority: seniorityEnum,
    description: z.string().min(JOB_LIMITS.descriptionLength[0]).max(JOB_LIMITS.descriptionLength[1]),
    competencies: z
        .array(z.string().min(1).max(JOB_LIMITS.competencyLength))
        .min(1)
        .max(JOB_LIMITS.competencies),
    questions: z.array(interviewQuestionInputSchema).min(1).max(JOB_LIMITS.questions),
    durationMinutes: z.number().int().min(5).max(60),
    voiceId: z.string().max(64).optional(),
    language: z.string().min(2).max(10),
    status: jobStatusEnum,
};
export const createJobSchema = z.object({
    ...jobFields,
    seniority: jobFields.seniority.default('mid'),
    durationMinutes: jobFields.durationMinutes.default(20),
    language: jobFields.language.default('en'),
    status: jobFields.status.default('open'),
});
/** Only the fields actually sent come back, so nothing unmentioned is overwritten. */
export const updateJobSchema = z.object(jobFields).partial();
export const candidateSchema = z.object({
    name: z.string().min(1).max(CANDIDATE_LIMITS.nameLength),
    email: z.string().email().max(CANDIDATE_LIMITS.emailLength),
    phone: z.string().max(40).optional(),
    background: z.string().max(CANDIDATE_LIMITS.backgroundLength).optional(),
});
export const createInterviewSchema = z.object({
    candidate: candidateSchema,
    /** How long the invite link stays valid. Defaults to a week. */
    expiresInHours: z.number().int().min(1).max(24 * 30).default(24 * 7),
});
export const hiringDecisionEnum = z.enum(['advance', 'hold', 'reject']);
/** Records a hiring decision; `null` clears it. */
export const decisionSchema = z.object({ outcome: hiringDecisionEnum.nullable() });
export const NOTE_MAX_LENGTH = 5000;
export const noteSchema = z.object({ text: z.string().trim().min(1).max(NOTE_MAX_LENGTH) });
/** Body the candidate page posts to exchange an invite token for a live session. */
export const startSessionSchema = z.object({
    interviewToken: z.string().min(10),
});
export const listQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().optional(),
});
/**
 * Shape the scoring model is constrained to. Kept in one place so the Bedrock
 * request, the DynamoDB item and the dashboard cannot drift apart.
 */
export const scorecardModelOutputSchema = z.object({
    overall: z.number().int().min(0).max(100),
    recommendation: z.enum(['strong_yes', 'yes', 'maybe', 'no']),
    competencyScores: z
        .array(z.object({
        competency: z.string(),
        score: z.number().int().min(1).max(5),
        evidence: z.string(),
    }))
        .min(1),
    strengths: z.array(z.string()),
    concerns: z.array(z.string()),
    redFlags: z.array(z.string()),
    summary: z.string(),
    followUpQuestions: z.array(z.string()),
    coverage: z.number().min(0).max(1),
});
/** Largest resume file accepted, before base64 encoding. */
export const RESUME_MAX_BYTES = 3 * 1024 * 1024;
export const resumeMediaTypeEnum = z.enum([
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
]);
/** Either an uploaded file (base64) or pasted text - never both. */
export const resumeBriefRequestSchema = z
    .object({
    fileName: z.string().max(255).optional(),
    mediaType: resumeMediaTypeEnum.optional(),
    data: z
        .string()
        .max(Math.ceil((RESUME_MAX_BYTES * 4) / 3) + 16)
        .optional(),
    text: z
        .string()
        .min(CANDIDATE_LIMITS.resumeTextLength[0])
        .max(CANDIDATE_LIMITS.resumeTextLength[1])
        .optional(),
})
    .refine((v) => Number(Boolean(v.data)) + Number(Boolean(v.text)) === 1, {
    message: 'Send either a resume file or pasted resume text',
})
    .refine((v) => !v.data || Boolean(v.mediaType), {
    message: 'mediaType is required when sending a file',
});
/**
 * Shape the resume model is constrained to. Deliberately no computed
 * "years of experience": in testing the model derived it from a graduation
 * year - an age signal by the back door - and got it wrong. The roles and
 * their dates carry the same information without the inference.
 */
export const resumeBriefModelOutputSchema = z.object({
    summary: z.string(),
    recentRoles: z.array(z.object({ title: z.string(), organisation: z.string(), period: z.string() })),
    skills: z.array(z.string()),
    notableWork: z.array(z.string()),
    claimsToProbe: z.array(z.string()),
    suspiciousContent: z.array(z.string()),
});
//# sourceMappingURL=schemas.js.map